<script setup lang="ts">
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useVisitorSession } from '~/stores/useVisitorSession'

definePageMeta({
  middleware: ['read-session-token-from-query'],
})

const { t, locale } = useI18n()
const visitorSession = useVisitorSession()
const isAdmin = computed(() => visitorSession.admin)

const defaultState = 'signup'
const dlLink = '/download'

const faqItems = [
  {
    key: 'shebei',
  },
  {
    key: 'yinsi',
  },
  {
    key: 'anquan',
  },
]

// Same order as the guides on /download (DownloadAndGuide.vue).
const platformLinks = [
  { label: 'Windows', query: 'Windows', icon: 'simple-icons:windows' },
  { label: 'macOS', query: 'macOS', icon: 'mdi:apple-finder' },
  { label: 'iOS', query: 'iOS', icon: 'simple-icons:apple' },
  { label: 'Android', query: 'Android', icon: 'simple-icons:android' },
  { label: 'Apple TV', query: 'Apple TV', icon: 'simple-icons:appletv' },
  { label: 'Linux', query: 'Linux', icon: 'simple-icons:linux' },
  { label: 'Router', labelZh: '路由器', query: 'Router', icon: 'cil:router' },
] as const

function platformLabel(platform: (typeof platformLinks)[number]) {
  return locale.value === 'zh' && 'labelZh' in platform ? platform.labelZh : platform.label
}

const marketingFeatures = [
  {
    icon: 'lucide:globe-2',
    title: 'index.feature.edge_network',
  },
  {
    icon: 'mdi:shield-check',
    title: 'index.feature.enterprise_security',
  },
  {
    icon: 'mdi:shield-lock',
    title: 'index.feature.no_logs',
  },
  {
    icon: 'mdi:source-branch',
    title: 'index.feature.smart_routing',
  },
] as const

const performanceStats = [
  {
    value: '99.99',
    unit: '%',
    title: 'index.stats.reliability',
    description: 'index.sub2.b',
  },
  {
    value: '256',
    unit: 'bit',
    title: 'index.feature.enterprise_security',
    description: 'index.feature.enterprise_security_desc',
  },
  {
    value: '0',
    unit: 'logs',
    title: 'index.feature.no_logs',
    description: 'index.feature.no_logs_desc',
  },
] as const

const activeAnalysisIndex = ref(0)
const landingRoot = useTemplateRef<HTMLElement>('landingRoot')

const expertisePanels = [
  {
    title: 'index.feature.years_experience',
    description: 'index.feature.years_desc',
  },
  {
    title: 'index.feature.edge_network',
    description: 'index.feature.edge_network_desc',
  },
  {
    title: 'index.feature.eubased',
    description: 'index.feature.eubased_desc',
  },
  {
    title: 'index.feature.secrecy_by_design',
    description: 'index.feature.secrecy_desc',
  },
] as const

const analysisDetails = [
  {
    title: 'index.locations.title',
    description: 'index.locations.subtitle',
    menuDescription: 'index.feature.edge_network_desc',
    bullets: ['index.feature.edge_network_desc', 'index.sub2.b'],
  },
  {
    title: 'index.feature.enterprise_security',
    description: 'index.feature.enterprise_security_desc',
    menuDescription: 'index.feature.enterprise_security_desc',
    bullets: ['index.analysis.protocol_options', 'index.analysis.app_router_client', 'index.analysis.setup_guidance'],
  },
  {
    title: 'index.feature.no_logs',
    description: 'index.feature.no_logs_desc',
    menuDescription: 'index.feature.no_logs_desc',
    bullets: ['index.analysis.no_activity_profiles', 'index.analysis.account_data_minimized'],
  },
  {
    title: 'index.feature.smart_routing',
    description: 'index.feature.smart_routing_desc',
    menuDescription: 'index.feature.smart_routing_desc',
    bullets: ['index.sub2.a', 'index.sub2.b'],
  },
] as const

const activeAnalysisFeature = computed(() => {
  const feature = marketingFeatures[activeAnalysisIndex.value]
  if (!feature) {
    throw new Error(`Missing analysis feature for index ${activeAnalysisIndex.value}`)
  }
  return feature
})

const activeAnalysisDetails = computed(() => {
  const details = analysisDetails[activeAnalysisIndex.value]
  if (!details) {
    throw new Error(`Missing analysis details for index ${activeAnalysisIndex.value}`)
  }
  return details
})

const pageTitle = computed(() => `${t('site_name')} Est. 2012 熊猫翻滚`)
const pageDescription = computed(() => `${t('index.sub.a')} ${t('index.sub.b')} ${t('index.sub.c')}`)

let revealObserver: IntersectionObserver | null = null
let motionQuery: MediaQueryList | null = null

function revealAll() {
  landingRoot.value?.querySelectorAll<HTMLElement>('.reveal').forEach((element) => {
    element.classList.remove('reveal-pending')
  })
  revealObserver?.disconnect()
}

function onMotionChange(event: MediaQueryListEvent) {
  if (event.matches) revealAll()
}

onMounted(() => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  motionQuery.addEventListener('change', onMotionChange)
  if (motionQuery.matches) return

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.remove('reveal-pending')
        revealObserver?.unobserve(entry.target)
      })
    },
    { threshold: 0.08 },
  )

  // Server-rendered content stays visible; only offscreen content waits for a reveal.
  landingRoot.value?.querySelectorAll<HTMLElement>('.reveal').forEach((element) => {
    if (element.getBoundingClientRect().top < window.innerHeight) return
    element.classList.add('reveal-pending')
    revealObserver?.observe(element)
  })
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
  motionQuery?.removeEventListener('change', onMotionChange)
})

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterCard: 'summary_large_image',
})

useHead({
  title: () => pageTitle.value,
})
</script>

<template lang="pug">
div.landing-shell(ref="landingRoot" :class="{ 'is-zh': locale === 'zh' }")
  a.skip-link(href="#home-main") {{ locale === 'zh' ? '跳至正文' : 'Skip to content' }}
  main#home-main.homepage(tabindex="-1")
    .home-frame
      section.hero-section
        .hero-grid
          .hero-heading
            p.eyebrow-label.hero-enter
              span.eyebrow-dot
              span PandaFan · {{ t('index.hero_badge') }}
            h1.hero-title.hero-enter
              span {{ t('index.hero_lead') }}
              span.hero-title-accent {{ t('index.hero_accent') }}
            p.hero-description.hero-enter
              span.hero-description-line {{ t('index.sub.a') }}
              span.hero-description-line {{ t('index.sub.b') }}
            .hero-cta-group.hero-enter
              .hero-actions
                SignButton.hero-sign-button(:defaultState="defaultState" buttonStyle="main" :redirectIfLoggedIn="true")
                NuxtLink.secondary-action(to="/pricing")
                  span {{ t('index.view_pricing') }}
                  Icon(name="lucide:arrow-up-right" aria-hidden="true")
              NuxtLink.hero-teams-link(to="/org")
                Icon(name="lucide:building-2" class="w-4 h-4 shrink-0" aria-hidden="true")
                span {{ t('index.teams_link') }}
                Icon.hero-teams-arrow(name="lucide:arrow-right" aria-hidden="true")
          .hero-art
            HomeConnectionGlobe
      .platform-block.reveal
        NuxtLink.platform-chip(
          v-for="platform in platformLinks"
          :key="platform.label"
          :to="`${dlLink}?os=${platform.query}`"
          :aria-label="platformLabel(platform)"
        )
          Icon.platform-device-icon(:name="platform.icon" aria-hidden="true")
          span.platform-label {{ platformLabel(platform) }}
          Icon.platform-arrow(name="lucide:arrow-up-right" aria-hidden="true")
      section.expertise-section.reveal(:aria-label="t('index.features.badge')")
        article.expertise-item(v-for="(panel, index) in expertisePanels" :key="panel.title")
          span.expertise-index 0{{ index + 1 }}
          h2 {{ index === 0 ? t('index.hero_badge') : t(panel.title) }}
          p {{ t(panel.description) }}
      section.stats-section.reveal
        .stats-grid
          article.stat-card(v-for="stat in performanceStats" :key="stat.title")
            p.stat-number
              span.stat-value {{ stat.value }}
              span.stat-unit {{ stat.unit }}
            h2.stat-title {{ t(stat.title) }}
            p.stat-copy {{ t(stat.description) }}
      section#home-features.features-section.reveal
        .analysis-panel
          .analysis-menu
            button.analysis-menu-item(
              v-for="(feature, index) in marketingFeatures"
              :key="feature.title"
              type="button"
              aria-controls="home-feature-detail"
              :aria-pressed="activeAnalysisIndex === index"
              :class="{ 'is-active': activeAnalysisIndex === index }"
              @click="activeAnalysisIndex = index"
              @focus="activeAnalysisIndex = index"
            )
              h3.analysis-menu-title {{ t(feature.title) }}
              p.analysis-menu-copy {{ t(analysisDetails[index].menuDescription) }}
          #home-feature-detail.analysis-detail
            .analysis-detail-content
              h2.analysis-title
                Icon.analysis-title-icon(:name="activeAnalysisFeature.icon" aria-hidden="true")
                span.analysis-title-text {{ t(activeAnalysisDetails.title) }}
              p.analysis-copy {{ t(activeAnalysisDetails.description) }}
              ul.analysis-bullets
                li.analysis-bullet(v-for="bullet in activeAnalysisDetails.bullets" :key="bullet")
                  span.analysis-bullet-dot
                  span {{ t(bullet) }}

    section.story-faq-wrap.reveal
      p.eyebrow-label.faq-eyebrow
        span {{ locale === 'zh' ? '帮助中心 / FAQ' : 'FAQ' }}
      h2.faq-title {{ t('faq.title') }}
      .faq-list
        Accordion(type="single", collapsible)
          AccordionItem.faq-item(v-for="faqItem in faqItems" :key="faqItem.key" :value="faqItem.key")
            AccordionTrigger.faq-trigger
              span.faq-question {{ $t(`faq.q.${faqItem.key}`) }}
              template(#icon)
                span.faq-plus +
            AccordionContent
              .faq-answer
                p {{ $t(`faq.a.${faqItem.key}`) }}

    section.final-cta.reveal
      .final-panel
        p.eyebrow-label PandaFan / {{ t('index.hero_badge') }}
        h2.final-title {{ t('index.final_device') }}
        p.final-copy {{ t('index.final_device_desc') }}
        .final-actions
          SignButton.final-sign-button(:defaultState="defaultState" buttonStyle="main" :redirectIfLoggedIn="true")
          NuxtLink.secondary-action(to="/pricing") {{ t('index.view_pricing') }}

  nav.admin-links-wrap(v-if="isAdmin" aria-label="管理快捷操作")
    .admin-links
      NuxtLink(to="/admin") 管理后台
      NuxtLink(to="/real-time-speed" target="_blank") 线路速度
      NuxtLink(to="/tr-abuser") 超流量检查
      NuxtLink(to="/over-limit") 共用检查
      NuxtLink(to="/pmw_transactions") PMW记录
      NuxtLink(to="/stripe_events") Stripe记录
      NuxtLink(to="/crypto_transactions") USDT记录
      NuxtLink(to="/servers") 修改服务器
</template>

<style scoped>
/* Keep Safari's page backdrop aligned with the light landing surface. */
:global(html.light:has(.landing-shell)) {
  --background: #f4f7f9;
}

/* The dotted layer uses black, while the default body surface is dark gray. */
:global(html.dark:has(.landing-shell) body) {
  background-color: #000;
}

.landing-shell {
  /* Metropolis is not shipped; name the system display faces so every platform gets a deliberate one. */
  --home-display: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI Variable Display', 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  --home-paper: var(--background);
  --home-surface: color-mix(in srgb, var(--card) 72%, transparent);
  --home-text: var(--primary-color);
  --home-copy: var(--secondary-color);
  --home-green: #7ed321;
  --home-border: color-mix(in srgb, var(--primary-color) 13%, transparent);
  --home-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --home-mono: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace;
  width: 100%;
  color: var(--home-text);
  /* Dark mode reveals the app-owned background texture. */
  background: transparent;
  font-family: var(--default-font);
}
.light .landing-shell {
  --home-green: #2d5a27;
  background: var(--home-paper);
}
.landing-shell :where(h1, h2, h3, p, span) {
  color: inherit;
  font-family: inherit;
}
.homepage {
  width: 100%;
  padding: 0;
}
.home-frame,
.story-faq-wrap,
.final-panel {
  width: var(--marketing-frame);
  margin-inline: auto;
}
.skip-link {
  position: fixed;
  z-index: 1001;
  top: 12px;
  left: 16px;
  padding: 12px 20px;
  background: var(--home-text);
  color: var(--home-paper);
  clip-path: inset(50%);
}
.skip-link:focus {
  clip-path: none;
}
a,
button {
  -webkit-tap-highlight-color: transparent;
}
a:focus-visible,
button:focus-visible,
:deep(button:focus-visible) {
  outline: 2px solid var(--home-green);
  outline-offset: 5px;
}
.hero-section {
  padding-top: clamp(48px, 5vw, 80px);
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 24px;
  align-items: center;
}
.hero-heading {
  min-width: 0;
  padding-block: 24px;
}
@media (min-width: 768px) {
  .landing-shell:not(.is-zh) .hero-grid {
    grid-template-columns: 1.4fr 1fr;
  }
}
.eyebrow-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--home-mono);
  font-size: 12px;
  letter-spacing: 0;
  color: var(--home-copy);
  line-height: 16px;
}
.eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--home-green);
}
.hero-title {
  margin: 24px 0;
  font: 500 clamp(48px, 5.4vw, 76px)/1.04 var(--home-display);
  letter-spacing: -0.03em;
}
.hero-title span {
  display: block;
  text-wrap: balance;
}
.hero-title-accent {
  color: inherit;
}
.is-zh .hero-title {
  font-size: clamp(56px, 7vw, 96px);
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: normal;
}
.hero-description {
  color: var(--home-copy);
  font-size: 18px;
  line-height: 28px;
  max-width: 36rem;
}
.hero-description-line {
  display: block;
}
.hero-cta-group {
  margin-top: 32px;
}
.hero-actions,
.final-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}
.hero-sign-button :deep(.sign-up),
.final-sign-button :deep(.sign-up) {
  margin: 0;
}
.hero-sign-button :deep(.sign-up button),
.final-sign-button :deep(.sign-up button) {
  height: 52px;
  padding: 0 30px;
  border: 1px solid var(--home-green);
  border-radius: 999px;
  box-shadow: none;
  background: var(--home-green);
  color: var(--home-paper) !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  transition:
    transform 250ms var(--home-ease),
    background-color 250ms;
}
.hero-sign-button :deep(.sign-up button:hover),
.final-sign-button :deep(.sign-up button:hover) {
  transform: translateY(-3px);
  background: color-mix(in srgb, var(--home-green), var(--home-text) 15%);
}
.hero-sign-button :deep(.sign-up button:active),
.final-sign-button :deep(.sign-up button:active) {
  transform: translateY(0);
}
.secondary-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  color: var(--home-text);
  font-size: 15px;
}
.secondary-action .iconify,
.hero-teams-arrow {
  transition: transform 250ms var(--home-ease);
}
.secondary-action:hover .iconify {
  transform: translate(2px, -2px);
}
.hero-teams-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  min-height: 32px;
  font-size: 14px;
  color: var(--home-copy);
  transition: color 150ms var(--home-ease);
}
.hero-teams-link:hover {
  color: var(--home-text);
}
.hero-teams-link:hover .hero-teams-arrow {
  transform: translateX(2px);
}
.hero-art {
  min-width: 0;
}
.platform-block {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-block: 1px solid var(--home-border);
}
.platform-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 8px;
  color: var(--home-copy);
  font-size: 14px;
  transition:
    color 150ms var(--home-ease),
    background-color 150ms var(--home-ease);
}
.platform-device-icon {
  width: 18px;
  height: 18px;
  flex: none;
  filter: none;
}
.platform-arrow {
  width: 12px;
  opacity: 0;
  transform: translate(-3px, 3px);
  transition:
    opacity 150ms var(--home-ease),
    transform 150ms var(--home-ease);
}
.platform-chip:hover,
.platform-chip:focus-visible {
  color: var(--home-text);
  background: var(--home-surface);
}
.platform-chip:hover .platform-arrow,
.platform-chip:focus-visible .platform-arrow {
  opacity: 1;
  transform: translate(0);
}
.expertise-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  padding-block: 64px;
}
.expertise-index {
  font: 12px/16px var(--home-mono);
  color: var(--home-copy);
}
.expertise-item h2 {
  margin: 16px 0 8px;
  font-size: 16px;
  font-weight: 500;
}
.expertise-item p {
  font-size: 14px;
  line-height: 20px;
  color: var(--home-copy);
}
.stats-section {
  padding-block: 80px;
  border-block: 1px solid var(--home-border);
}
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.stat-card {
  min-height: 12rem;
  padding: 32px 48px 32px 0;
}
.stat-card + .stat-card {
  border-left: 1px solid var(--home-border);
  padding-left: 48px;
}
.stat-number {
  display: flex;
  align-items: baseline;
  margin-bottom: 16px;
  font-family: var(--home-display);
  font-size: clamp(3.5rem, 5vw, 4.5rem);
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  white-space: pre;
}
.stat-value {
  font: inherit;
}
.stat-unit {
  color: var(--home-copy);
  font: inherit;
  font-size: 0.5em;
  letter-spacing: -0.01em;
}
.stat-title {
  margin-bottom: 8px;
  color: var(--home-text);
  font-size: 15px;
  font-weight: 500;
}
.stat-copy {
  max-width: 18rem;
  font-size: 14px;
  line-height: 20px;
  color: var(--home-copy);
}
.faq-title {
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.3;
  margin-top: 18px;
  text-wrap: balance;
}
.story-faq-wrap {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  column-gap: 64px;
  border-top: 1px solid var(--home-border);
  padding-block: 64px 96px;
}
.faq-eyebrow {
  grid-column: 1;
}
.faq-title {
  grid-column: 1;
  align-self: start;
  margin-top: 16px;
}
.faq-list {
  grid-column: 2;
  grid-row: 1 / 4;
}
.faq-item {
  border-color: var(--home-border);
}
:deep(.faq-trigger) {
  padding: 24px 0;
  gap: 24px;
  color: var(--home-text);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.7;
  text-align: left;
}
:deep(.faq-trigger:hover) {
  text-decoration: none;
  color: var(--home-copy);
}
:deep(.faq-plus) {
  flex: none;
  font-size: 24px;
  font-weight: 300;
  color: var(--home-copy);
  transition: transform 250ms var(--home-ease);
}
:deep(.faq-trigger[data-state='open'] .faq-plus) {
  transform: rotate(45deg);
}
.faq-answer {
  padding: 0 36px 16px 0;
  color: var(--home-copy);
  font-size: 15px;
  line-height: 24px;
  white-space: pre-line;
}
.final-cta {
  background: transparent;
  border-top: 1px solid var(--home-border);
  padding-block: 80px;
}
.final-panel {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px 64px;
  align-items: center;
}
.final-panel .eyebrow-label {
  grid-column: 1 / -1;
}
.final-title {
  font: 500 clamp(30px, 3.7vw, 48px)/1.15 var(--home-display);
  letter-spacing: -0.02em;
  max-width: 19em;
  text-wrap: balance;
}
.final-copy {
  grid-column: 1;
  max-width: 40em;
  font-size: 15px;
  line-height: 24px;
  color: var(--home-copy);
}
.final-actions {
  grid-column: 2;
  grid-row: 2 / 4;
}
.admin-links-wrap {
  position: fixed;
  left: 50%;
  bottom: calc(1.5rem + env(safe-area-inset-bottom));
  z-index: 60;
  transform: translateX(-50%);
  width: min(calc(100% - 32px), 58rem);
  padding: 6px;
  border: 1px solid var(--home-border);
  border-radius: 16px;
  background: color-mix(in srgb, var(--home-paper) 92%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 6px 24px #0000000d;
}
.admin-links {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 4px;
}
.admin-links a {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 4px;
  border-radius: 10px;
  color: var(--home-copy);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  transition:
    color 180ms ease,
    background-color 180ms ease;
}
.admin-links a:hover,
.admin-links a:focus-visible {
  color: var(--home-green);
  background: color-mix(in srgb, var(--home-green) 9%, transparent);
}
.admin-links a:focus-visible {
  outline-offset: -2px;
}
@media (max-width: 1080px) {
  .admin-links-wrap {
    /* Leave room for the fixed support launcher when the toolbar spans the viewport. */
    bottom: calc(5.5rem + env(safe-area-inset-bottom));
  }
}
.hero-enter {
  animation: hero-enter 500ms var(--home-ease) both;
}
.hero-title.hero-enter {
  animation-delay: 50ms;
}
.hero-description.hero-enter {
  animation-delay: 100ms;
}
.hero-cta-group.hero-enter {
  animation-delay: 150ms;
}
.reveal {
  transition:
    opacity 500ms var(--home-ease),
    transform 500ms var(--home-ease);
}
.reveal-pending {
  opacity: 0;
  transform: translateY(12px);
}
.reveal-pending:focus-within {
  opacity: 1;
  transform: none;
}
@keyframes hero-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (max-width: 1100px) {
  .hero-title {
    font-size: clamp(42px, 5.7vw, 64px);
  }
}
@media (max-width: 1023px) {
  .stat-card {
    padding-right: 1.5rem;
  }
  .stat-card + .stat-card {
    padding-left: 1.5rem;
  }
  .stat-number {
    font-size: clamp(3rem, 6.35vw, 4.5rem);
  }
}
@media (max-width: 767px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .stat-card,
  .stat-card + .stat-card {
    min-height: 0;
    padding: 1.7rem 0;
    border-left: 0;
    border-bottom: 1px solid var(--home-border);
  }
  .stat-card:last-child {
    border-bottom: 0;
  }
  .admin-links {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .admin-links a {
    min-height: 40px;
    font-size: 11px;
  }
  .hero-section {
    padding-top: 40px;
  }
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .hero-heading {
    padding: 0;
  }
  .hero-title {
    font-size: clamp(40px, 10.5vw, 56px);
    margin-block: 20px;
  }
  .is-zh .hero-title {
    font-size: clamp(44px, 14vw, 80px);
    max-width: none;
  }
  .hero-description {
    font-size: 16px;
    line-height: 24px;
  }
  .hero-cta-group {
    margin-top: 24px;
  }
  .hero-art {
    width: min(88%, 400px);
    margin: 0 auto;
  }
  .platform-block {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  .platform-chip {
    flex: 0 0 25%;
    padding: 20px 0;
    font-size: 13px;
    gap: 8px;
  }
  .platform-chip:nth-child(-n + 4) {
    border-bottom: 1px solid var(--home-border);
  }
  .platform-arrow {
    display: none;
  }
  .expertise-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px 24px;
    padding-block: 40px;
  }
  .expertise-item h2 {
    margin-top: 12px;
    font-size: 15px;
  }
  .stats-section {
    padding-block: 1.5rem;
  }
  .stat-number {
    font-size: clamp(3.5rem, 15vw, 4.5rem);
  }
  .story-faq-wrap {
    display: block;
    padding-block: 40px 56px;
  }
  .faq-list {
    margin-top: 24px;
  }
  :deep(.faq-trigger) {
    font-size: 14px;
    gap: 20px;
  }
  .final-cta {
    padding-block: 48px;
  }
  .final-panel {
    grid-template-columns: 1fr;
  }
  .final-title {
    font-size: 32px;
  }
  .final-actions {
    grid-column: 1;
    grid-row: auto;
    margin-top: 12px;
  }
}
@media (prefers-reduced-motion: reduce) {
  :global(html:has(.landing-shell)) {
    scroll-behavior: auto;
  }
  *,
  *::before,
  *::after,
  :deep(*) {
    animation: none !important;
    transition: none !important;
  }
  .reveal-pending {
    opacity: 1;
    transform: none;
  }
}
.features-section {
  padding-block: 3.8rem 1.2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.analysis-panel {
  display: grid;
  grid-template-columns: minmax(20rem, 0.5fr) minmax(0, 1fr);
  min-height: 36rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: #111311;
}
.analysis-menu {
  display: grid;
  grid-template-rows: repeat(4, minmax(0, 1fr));
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: #020402;
}
.analysis-menu-item {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.95rem;
  padding: 2rem 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: #020402;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition:
    background-color 150ms var(--home-ease),
    color 150ms var(--home-ease);
}
.analysis-menu-item:last-child {
  border-bottom: 0;
}
.analysis-menu-item::after {
  content: '';
  position: absolute;
  top: 0;
  right: -1px;
  width: 2px;
  height: 100%;
  background: #7ed321;
  opacity: 0;
  transform: scaleY(0.72);
  transform-origin: center;
  transition:
    opacity 300ms var(--home-ease),
    transform 300ms var(--home-ease);
}
.analysis-menu-item.is-active {
  background: #121412;
}
.analysis-menu-item.is-active::after {
  opacity: 1;
  transform: scaleY(1);
}
.analysis-menu-item:not(.is-active):hover {
  background: #0a0c0a;
}
.analysis-menu-item:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.72);
  outline-offset: -3px;
}
.analysis-menu-title {
  max-width: 16ch;
  color: #ededed;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
}
.analysis-menu-copy {
  max-width: none;
  color: #a1a1a6;
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.analysis-detail {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: clamp(3rem, 5vw, 5.4rem);
  background-color: #121412;
  background-image: linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px);
  background-size: 12.5% 10rem;
}

.analysis-detail-content {
  position: relative;
  z-index: 1;
  max-width: 46rem;
}
.analysis-title {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  max-width: 14ch;
  color: #ededed;
  font-size: 2.25rem;
  font-weight: 500;
  line-height: 1.12;
  letter-spacing: -0.02em;
}
.analysis-title-icon {
  flex: none;
  color: #a1a1a6;
  font-size: 1.75rem;
}
.analysis-title-text {
  white-space: pre-line;
}
.analysis-copy {
  max-width: 42rem;
  margin-top: 1.5rem;
  color: #a1a1a6;
  font-size: 1rem;
  line-height: 1.625;
}
.analysis-bullets {
  display: grid;
  gap: 1rem;
  margin-top: 2.5rem;
  padding: 0;
  list-style: none;
}
.analysis-bullet {
  display: grid;
  grid-template-columns: 0.55rem minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  color: #ededed;
  font-size: 0.9375rem;
  line-height: 1.5rem;
}
.analysis-bullet-dot {
  align-self: center;
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  background: #7ed321;
}
.light .landing-shell .analysis-title-icon {
  color: var(--home-copy);
}
.light .landing-shell .analysis-title,
.light .landing-shell .analysis-menu-title {
  color: var(--home-text);
}
.light .landing-shell .analysis-copy,
.light .landing-shell .analysis-menu-copy {
  color: var(--home-copy);
}
.light .landing-shell .features-section {
  border-color: #e2e8f0;
}
.light .landing-shell .analysis-panel {
  border-color: #e2e8f0;
  background: var(--home-surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.light .landing-shell .analysis-menu {
  border-color: #e2e8f0;
  background: var(--home-surface);
}
.light .landing-shell .analysis-menu-item {
  border-color: #f1f5f9;
  background: var(--home-surface);
}
.light .landing-shell .analysis-menu-item.is-active {
  background: var(--home-surface-soft);
}
.light .landing-shell .analysis-menu-item:not(.is-active):hover {
  background: var(--home-surface-soft);
}
.light .landing-shell .analysis-menu-item::after {
  background: var(--home-green);
}
.light .landing-shell .analysis-detail {
  background-color: var(--home-surface-soft);
  background-image: linear-gradient(var(--home-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--home-grid-line) 1px, transparent 1px);
  background-size: var(--home-grid-size) var(--home-grid-size);
}
.light .landing-shell .analysis-bullet {
  color: #475569;
}
.light .landing-shell .analysis-bullet-dot {
  background: var(--home-green);
  box-shadow: none;
}
@media (max-width: 1023px) {
  .analysis-panel {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .analysis-menu {
    grid-template-rows: none;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .analysis-menu-item {
    min-height: 12rem;
    padding: 1.6rem;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    border-bottom: 0;
  }

  .analysis-menu-item:last-child {
    border-right: 0;
  }

  .analysis-menu-item::after {
    top: auto;
    right: 0;
    bottom: -1px;
    width: 100%;
    height: 3px;
    transform: scaleX(0.72);
  }

  .analysis-menu-item.is-active::after {
    transform: scaleX(1);
  }

  .analysis-detail {
    min-height: 28rem;
  }
}
@media (max-width: 767px) {
  .features-section {
    padding-block: 2.7rem 0.6rem;
  }

  .analysis-panel {
    margin-inline: -1rem;
    border-radius: 0;
  }

  .analysis-menu {
    grid-template-columns: 1fr;
  }

  .analysis-menu-item {
    min-height: 9.4rem;
    gap: 0.6rem;
    padding: 1.35rem 1.5rem;
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .analysis-menu-item:last-child {
    border-bottom: 0;
  }

  .analysis-menu-title {
    max-width: none;
    font-size: 1.3rem;
  }

  .analysis-menu-copy {
    font-size: 0.86rem;
  }

  .analysis-detail {
    min-height: 28rem;
    padding: 2.4rem 1.5rem;
    background-size: 33.33% 7rem;
  }

  .analysis-title {
    max-width: 12ch;
    font-size: 2.25rem;
  }

  .analysis-copy {
    margin-top: 1.3rem;
    font-size: 1rem;
  }

  .analysis-bullets {
    margin-top: 2rem;
  }

  .analysis-bullet {
    font-size: 0.82rem;
  }
}
.light .landing-shell .analysis-menu,
.light .landing-shell .analysis-menu-item {
  border-color: #e2e8f0;
}
@media (max-width: 1023px) {
  .light .landing-shell .analysis-menu {
    border-bottom-color: #e2e8f0;
  }

  .light .landing-shell .analysis-menu-item {
    border-right-color: #e2e8f0;
    border-bottom-color: #e2e8f0;
  }
}
.landing-shell .analysis-menu-title,
.landing-shell .analysis-title {
  max-width: 100%;
  white-space: normal;
  text-wrap: wrap;
  overflow-wrap: normal;
}
@media (prefers-reduced-motion: reduce) {
  .analysis-menu-item {
    transition: none;
  }
}
.features-section {
  scroll-margin-top: 88px;
}
.light .landing-shell .features-section {
  --home-surface: #ffffff;
  --home-surface-soft: #f8fafc;
  --home-text: #1e293b;
  --home-copy: #64748b;
  --home-grid-line: rgba(51, 65, 85, 0.08);
  --home-grid-size: 30px;
}
</style>
